import { Mail, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';

// Cookie helper functions
const setCookie = (name: string, value: string, days: number) => {
  const expires = new Date();
  expires.setTime(expires.getTime() + (days * 24 * 60 * 60 * 1000));
  document.cookie = `${name}=${value};expires=${expires.toUTCString()};path=/`;
};

const getCookie = (name: string): string | null => {
  const nameEQ = name + "=";
  const ca = document.cookie.split(';');
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i];
    while (c.charAt(0) === ' ') c = c.substring(1, c.length);
    if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
  }
  return null;
};

export function NewsletterBar() {
  const [email, setEmail] = useState('');
  const [isVisible, setIsVisible] = useState(false); // Start as false, will be set based on cookie
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isConnectedToFooter, setIsConnectedToFooter] = useState(false);
  const newsletterRef = useRef<HTMLDivElement>(null);

  // Check cookie on mount
  useEffect(() => {
    const newsletterDismissed = getCookie('newsletter_dismissed');
    const newsletterSubscribed = getCookie('newsletter_subscribed');
    
    // Only show if user hasn't dismissed or subscribed
    if (!newsletterDismissed && !newsletterSubscribed) {
      setIsVisible(true);
    }
  }, []);

  useEffect(() => {
    let ticking = false;
    
    const checkFooterConnection = () => {
      if (!newsletterRef.current || ticking) return;
      
      ticking = true;
      requestAnimationFrame(() => {
        if (!newsletterRef.current) {
          ticking = false;
          return;
        }

        const newsletterRect = newsletterRef.current.getBoundingClientRect();
        const footer = document.querySelector('footer');
        
        if (footer) {
          const footerRect = footer.getBoundingClientRect();
          
          // Check if newsletter bar is touching or overlapping the footer with larger tolerance
          const isConnected = newsletterRect.bottom >= footerRect.top - 10;
          setIsConnectedToFooter(prev => prev !== isConnected ? isConnected : prev);
        }
        
        ticking = false;
      });
    };

    // Debounced scroll handler
    let scrollTimeout: number;
    const debouncedCheck = () => {
      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(checkFooterConnection, 16); // ~60fps
    };

    checkFooterConnection();
    window.addEventListener('scroll', debouncedCheck, { passive: true });
    window.addEventListener('resize', checkFooterConnection);

    return () => {
      clearTimeout(scrollTimeout);
      window.removeEventListener('scroll', debouncedCheck);
      window.removeEventListener('resize', checkFooterConnection);
    };
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;

    setIsSubmitting(true);
    
    try {
      const response = await fetch('https://formspree.io/f/xldlvwak', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      if (response.ok) {
        setIsSubmitted(true);
        setEmail('');
        // Set cookie to remember user subscribed (30 days)
        setCookie('newsletter_subscribed', 'true', 30);
        // Track newsletter signup
        if (typeof window !== 'undefined' && (window as any).gtag) {
          (window as any).gtag('event', 'newsletter_signup', {
            event_category: 'engagement',
            event_label: 'sticky_footer_bar'
          });
        }
      } else {
        console.error('Failed to subscribe');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
    } finally {
      setIsSubmitting(false);
    }
    
    // Hide after success
    setTimeout(() => {
      setIsVisible(false);
    }, 3000);
  };

  if (!isVisible) return null;

  return (
    <div 
      ref={newsletterRef}
      className={`sticky bottom-0 z-40 border-t backdrop-blur transition-all duration-300 ease-in-out ${
        isConnectedToFooter 
          ? 'border-slate-700 bg-slate-900 text-slate-100' 
          : 'border-[#4A90E2]/30 bg-[#4A90E2] text-white'
      }`}
    >
      <div className="container py-3">
        {/* Desktop Layout */}
        <div className="hidden sm:flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 flex-1">
            <Mail className={`h-5 w-5 flex-shrink-0 ${
              isConnectedToFooter ? 'text-slate-100' : 'text-white'
            }`} />
            <div className="flex-1">
              <p className={`text-sm font-medium ${
                isConnectedToFooter ? 'text-slate-100' : 'text-white'
              }`}>
                {isSubmitted ? 'Thanks for subscribing!' : 'Stay updated with AI literacy news'}
              </p>
              {!isSubmitted && (
                <p className={`text-xs ${
                  isConnectedToFooter ? 'text-slate-300' : 'text-white/80'
                }`}>
                  Daily curated news and prompt engineering insights. No spam, unsubscribe anytime.
                </p>
              )}
            </div>
          </div>
          
          {!isSubmitted ? (
            <form onSubmit={handleSubmit} className="flex items-center gap-2">
              <Input
                type="email"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className={`w-48 h-8 text-sm transition-colors ${
                  isConnectedToFooter 
                    ? '!bg-slate-800 border-slate-600 text-slate-100 placeholder:text-slate-400' 
                    : '!bg-white border-white/30 text-slate-800 placeholder:text-slate-500'
                }`}
                disabled={isSubmitting}
                required
              />
              <Button 
                type="submit" 
                size="sm" 
                disabled={isSubmitting}
                className={`h-8 transition-colors ${
                  isConnectedToFooter
                    ? 'bg-primary hover:bg-primary/90 text-white border-primary'
                    : 'bg-primary hover:bg-primary/90 text-white border-primary'
                }`}
              >
                {isSubmitting ? 'Subscribing...' : 'Subscribe'}
              </Button>
            </form>
          ) : (
            <div className={`text-sm font-medium ${
              isConnectedToFooter ? 'text-slate-100' : 'text-white'
            }`}>
              ✓ You're all set!
            </div>
          )}
          
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              // Set cookie to remember user dismissed (7 days)
              setCookie('newsletter_dismissed', 'true', 7);
              setIsVisible(false);
            }}
            className={`h-8 w-8 p-0 flex-shrink-0 transition-colors ${
              isConnectedToFooter 
                ? 'text-slate-300 hover:text-slate-100 hover:bg-white/10' 
                : 'text-white/70 hover:text-white hover:bg-white/20'
            }`}
          >
            <X className="h-4 w-4" />
            <span className="sr-only">Close newsletter signup</span>
          </Button>
        </div>

        {/* Mobile Layout */}
        <div className="sm:hidden">
          <div className="flex items-start justify-between gap-3 mb-3">
            <div className="flex items-center gap-2 flex-1">
              <Mail className={`h-4 w-4 flex-shrink-0 ${
                isConnectedToFooter ? 'text-slate-100' : 'text-white'
              }`} />
              <div className="flex-1">
                <p className={`text-sm font-medium leading-tight ${
                  isConnectedToFooter ? 'text-slate-100' : 'text-white'
                }`}>
                  {isSubmitted ? 'Thanks for subscribing!' : 'Stay updated with AI literacy news'}
                </p>
              </div>
            </div>
            
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                // Set cookie to remember user dismissed (7 days)
                setCookie('newsletter_dismissed', 'true', 7);
                setIsVisible(false);
              }}
              className={`h-6 w-6 p-0 flex-shrink-0 transition-colors ${
                isConnectedToFooter 
                  ? 'text-slate-300 hover:text-slate-100 hover:bg-white/10' 
                  : 'text-white/70 hover:text-white hover:bg-white/20'
              }`}
            >
              <X className="h-3 w-3" />
              <span className="sr-only">Close newsletter signup</span>
            </Button>
          </div>
          
          {!isSubmitted ? (
            <form onSubmit={handleSubmit} className="flex gap-2">
              <Input
                type="email"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className={`flex-1 h-8 text-sm transition-colors ${
                  isConnectedToFooter 
                    ? '!bg-slate-800 border-slate-600 text-slate-100 placeholder:text-slate-400' 
                    : '!bg-white border-white/30 text-slate-800 placeholder:text-slate-500'
                }`}
                disabled={isSubmitting}
                required
              />
              <Button 
                type="submit" 
                size="sm" 
                disabled={isSubmitting}
                className={`h-8 px-3 text-xs transition-colors ${
                  isConnectedToFooter
                    ? 'bg-primary hover:bg-primary/90 text-white border-primary'
                    : 'bg-primary hover:bg-primary/90 text-white border-primary'
                }`}
              >
                {isSubmitting ? 'Subscribing...' : 'Subscribe'}
              </Button>
            </form>
          ) : (
            <div className={`text-sm font-medium text-center ${
              isConnectedToFooter ? 'text-slate-100' : 'text-white'
            }`}>
              ✓ You're all set!
            </div>
          )}
        </div>
      </div>
    </div>
  );
}